import { StepActionResponse, useGetApplicationByIdQuery } from "pages/application/applicationService";
import { hasBlock, getBlockType } from "pages/application/templates/Summary/queries";
import { useSelector } from "react-redux";
import { getCurrentUserApplicationId } from "features/authSlice";

export const useGetIncomeQuery = (blockType: "financesApplicantIncome" | "financesSpouseCoApplicantIncome") => {
  const applicationId = useSelector(getCurrentUserApplicationId);
  return useGetApplicationByIdQuery(applicationId, {
    selectFromResult: ({ data }) => {
      if (!hasBlock(data, blockType)) {
        return {
          incomeLabel: "",
          name: "",
          electronicallyVerifiedIncomes: [],
          manuallyListedIncomes: [],
          addIncomeAction: undefined,
        };
      }
      const { label: incomeLabel, description: name, dataItems, actions } = getBlockType(data, blockType);

      const incomes = dataItems.reduce<Income>(
        (acc, { label, fields, actions, dataItemType }) => {
          const isElectronicallyVerifiedIncome = dataItemType === "electronicallyVerifiedIncome";
          if (isElectronicallyVerifiedIncome) {
            const income: ElectronicallyVerifiedIncome = {
              category: label,
              source: fields[0].value,
              amount: fields[1].value,
              removeAction: actions.find(({ actionType }) => actionType === "remove")!,
            };
            acc.electronicallyVerifiedIncomes.push(income);
          } else {
            const income: ManuallyListedIncome = {
              category: label,
              source: fields[0].value,
              amount: fields[1].value,
              editAction: actions.find(({ actionType }) => actionType === "edit")!,
              removeAction: actions.find(({ actionType }) => actionType === "remove")!,
            };
            acc.manuallyListedIncomes.push(income);
          }
          return acc;
        },
        {
          electronicallyVerifiedIncomes: [],
          manuallyListedIncomes: [],
        }
      );

      const addIncomeAction = actions.find(({ actionType }) => actionType === "add")!;

      return {
        incomeLabel,
        name,
        ...incomes,
        addIncomeAction,
      };
    },
  });
};

export const useGetAssetsQuery = () => {
  const applicationId = useSelector(getCurrentUserApplicationId);
  return useGetApplicationByIdQuery(applicationId, {
    selectFromResult: ({ data }) => {
      if (!hasBlock(data, "financesAssets")) {
        return {
          assetsLabel: "",
          electronicallyVerifiedAssets: [],
          manuallyListedAssets: [],
          addAssetAction: undefined,
        };
      }
      const { label: assetsLabel, dataItems, actions } = getBlockType(data, "financesAssets");

      const assets = dataItems.reduce<Assets>(
        (acc, { label, fields, actions, dataItemType }) => {
          const isElectronicallyVerifiedAsset = dataItemType === "electronicallyVerifiedAsset";
          if (isElectronicallyVerifiedAsset) {
            const asset: ElectronicallyVerifiedAsset = {
              category: label,
              source: fields[0].value,
              amount: fields[1].value,
              assetType: !(fields[2].value.includes("Gift") || fields[2].value.includes("Other")) ? fields[2].value : "",
            };
            acc.electronicallyVerifiedAssets.push(asset);
          } else {
            const asset: ManuallyListedAsset = {
              category: label,
              source: fields[0].value,
              amount: fields[1].value,
              assetType: !(fields[2].value.includes("Gift") || fields[2].value.includes("Other")) ? fields[2].value : "",
              editAction: actions.find(({ actionType }) => actionType === "edit")!,
              removeAction: actions.find(({ actionType }) => actionType === "remove")!,
            };
            acc.manuallyListedAssets.push(asset);
          }
          return acc;
        },
        {
          electronicallyVerifiedAssets: [],
          manuallyListedAssets: [],
        }
      );

      const addAssetAction = actions.find(({ actionType }) => actionType === "add")!;

      return {
        assetsLabel,
        ...assets,
        addAssetAction,
      };
    },
  });
};

interface ElectronicallyVerifiedAsset {
  category: string;
  source: string;
  assetType: string;
  amount: string;
}

interface ManuallyListedAsset {
  category: string;
  source: string;
  assetType: string;
  amount: string;
  editAction: {
    stepActionId: string;
    label: string;
  };
  removeAction: {
    stepActionId: string;
    label: string;
  };
}

interface Assets {
  electronicallyVerifiedAssets: ElectronicallyVerifiedAsset[];
  manuallyListedAssets: ManuallyListedAsset[];
}

interface ManuallyListedIncome {
  category: string;
  source: string;
  amount: string;
  editAction: {
    stepActionId: string;
    label: string;
    actionType: StepActionResponse;
    presentationOrder: number;
  };
  removeAction: {
    stepActionId: string;
    label: string;
    actionType: StepActionResponse;
    presentationOrder: number;
  };
}

interface ElectronicallyVerifiedIncome {
  category: string;
  source: string;
  amount: string;
  removeAction: {
    stepActionId: string;
    label: string;
    actionType: StepActionResponse;
    presentationOrder: number;
  };
}

interface Income {
  electronicallyVerifiedIncomes: ElectronicallyVerifiedIncome[];
  manuallyListedIncomes: ManuallyListedIncome[];
}
